import axios from 'axios';
import { SettlementStep } from '../../models/settlementTypes';
import {
  FEE_METHOD,
  EXPENSE_ARRANGEMENT,
  API_FEE_METHOD,
  API_EXPENSE_ARRANGEMENT,
} from '../../constants/settlementConstants';
import type {
  SettlementResponse,
  UpdateSettlementRequest,
  SettlementSettings,
  DepositsResponse,
  ConfirmDepositPayload,
  ApiSettlementResponse,
  ApiSettlementStep,
  ApiSettlementValueStatus,
  ApiUpdateSettlementRequest,
  ApiCreateSettlementRequest,
  ApiExpenseArrangement,
  ApiFeeCalculationMethod,
  MatterExpenseListResponse,
  ConfirmExpensesRequest,
  ConfirmExpenseInterestRequest,
  FeeMethod,
  ExpenseArrangement,
  ConfirmLiensRequest,
  FromTableLiensResponse,
  ApiAttorneyFeeResponse,
  ApiFeeEntry,
  RecordFeeRequest,
  ConfirmAttorneyFeePayload,
  ClientPaymentsApiResponse,
  ConfirmClientPaymentsPayload,
  SettlementKeywordInfo,
  SettlementStatementTemplateDto,
  NarrativeContentInput,
  SettlementStatementNarrativeDto,
  EditorLockDto,
  ApiBlockingSettlement,
  ApiBlockingSettlementsResponse,
  ApiClientSettlementsResetResponse,
  BlockingSettlement,
} from '../../models/settlementTypes';

export interface TrustBalanceResponse {
  balance: number;
}

export interface PostSettlementActionResponse {
  settlement: SettlementResponse;
  message: string | null;
}

const buildUrl = (matterId: string): string =>
  `/matters/${matterId}/settlement`;

// Fallback for older/partial responses where summary.xStatus is missing or null (SC-45815):
// derive confirmation from the step's own status instead of assuming "still estimated".
export const isStepConfirmed = (
  steps: ApiSettlementStep[] | undefined,
  stepNumber: number,
): boolean => {
  const status = steps
    ?.find((s) => s.stepNumber === stepNumber)
    ?.status?.toLowerCase();
  return status === 'completed' || status === 'skipped';
};

const isValueConfirmed = (status: ApiSettlementValueStatus | null | undefined): boolean =>
  status?.toLowerCase() === 'confirmed';

// ── Enum Mapping (case-insensitive via lowercase keys) ───
// The backend returns enums in both PascalCase and lowercase variants
// (for backward compatibility across API versions). We normalize to frontend format.
const API_TO_FE_EXPENSE: Record<string, ExpenseArrangement> = {
  [API_EXPENSE_ARRANGEMENT.FIRM_ADVANCES.toLowerCase()]: EXPENSE_ARRANGEMENT.FIRM,
  [API_EXPENSE_ARRANGEMENT.CLIENT_PAYS.toLowerCase()]: EXPENSE_ARRANGEMENT.CLIENT,
  [API_EXPENSE_ARRANGEMENT.FIRM_ABSORBS.toLowerCase()]: EXPENSE_ARRANGEMENT.ABSORBED,
};

const FE_TO_API_EXPENSE: Record<string, ApiExpenseArrangement> = {
  [EXPENSE_ARRANGEMENT.FIRM]: API_EXPENSE_ARRANGEMENT.FIRM_ADVANCES,
  [EXPENSE_ARRANGEMENT.CLIENT]: API_EXPENSE_ARRANGEMENT.CLIENT_PAYS,
  [EXPENSE_ARRANGEMENT.ABSORBED]: API_EXPENSE_ARRANGEMENT.FIRM_ABSORBS,
};

const FE_TO_API_FEE_METHOD: Record<string, ApiFeeCalculationMethod> = {
  [FEE_METHOD.NET]: API_FEE_METHOD.NET,
  [FEE_METHOD.GROSS]: API_FEE_METHOD.GROSS,
};

const API_TO_FE_FEE_METHOD: Record<string, FeeMethod> = {
  [API_FEE_METHOD.NET]: FEE_METHOD.NET,
  [API_FEE_METHOD.GROSS]: FEE_METHOD.GROSS,
};

/**
 * Map the backend API response into the internal frontend shape
 * used by the store and components.
 */
export function mapApiToFrontend(
  api: ApiSettlementResponse,
): SettlementResponse {
  const nullGuid = '00000000-0000-0000-0000-000000000000';
  return {
    settlementId: api.settlementId === nullGuid ? null : api.settlementId,
    matterId: api.matterId,
    status: api.status,
    matterName: '',
    clientName: '',
    feePercentage: api.contingencyFeePercentage,
    feeMethod: api.feeCalculationMethod
      ? (API_TO_FE_FEE_METHOD[api.feeCalculationMethod.toLowerCase()] ??
        (api.feeCalculationMethod as FeeMethod))
      : null,
    expenseArrangement: api.expenseArrangement
      ? (API_TO_FE_EXPENSE[api.expenseArrangement.toLowerCase()] ?? null)
      : null,
    recoveryAmount: api.summary?.recoveryAmount ?? null,
    selectedDepositIds: api.selectedDepositIds ?? [],
    roundToNearestDollar: api.roundFeeToNearestDollar ?? false,
    computed: {
      adjustedRecovery: api.summary?.feeBase ?? null,
      fee: api.summary?.attorneyFee ?? null,
      netAttorneyFee: api.summary?.netAttorneyFee ?? null,
      clientNet: api.summary?.clientNetRecovery ?? null,
      unreimbursedExpenses: api.summary?.unreimbursedExpenses ?? null,
      lienTotal: api.summary?.totalLiens ?? null,
      totalExpenses: api.summary?.totalExpenses ?? null,
      alreadyPaidByClient: api.summary?.alreadyPaidByClient ?? null,
      calculationVariant: api.summary?.calculationVariant ?? null,
      salesTax: api.summary?.salesTax ?? null,
      taxComponents: api.summary?.taxComponents ?? [],
      expenseInterest: api.summary?.expenseInterest ?? null,
      coCounselExpensesTotal: api.summary?.coCounselExpensesTotal ?? null,
      generatingFirmRetainedAmount: api.summary?.generatingFirmRetainedAmount ?? null,
      // Backend reports xStatus (SC-45815) as 'Estimated' | 'Confirmed'; collapse to isXConfirmed
      // so the frontend never needs to compare strings at the point of use. Missing/null (older or
      // partial responses) falls back to the corresponding step's own completion status rather
      // than assuming "still estimated".
      isExpensesConfirmed:
        api.summary?.totalExpensesStatus != null
          ? isValueConfirmed(api.summary.totalExpensesStatus)
          : isStepConfirmed(api.steps, SettlementStep.Expense),
      isLiensConfirmed:
        api.summary?.totalLiensStatus != null
          ? isValueConfirmed(api.summary.totalLiensStatus)
          : isStepConfirmed(api.steps, SettlementStep.Lien),
      isAttorneyFeeConfirmed:
        api.summary?.attorneyFeeStatus != null
          ? isValueConfirmed(api.summary.attorneyFeeStatus)
          : isStepConfirmed(api.steps, SettlementStep.AttorneyFee),
      isClientNetRecoveryConfirmed:
        api.summary?.clientNetRecoveryStatus != null
          ? isValueConfirmed(api.summary.clientNetRecoveryStatus)
          : isStepConfirmed(api.steps, SettlementStep.Deposit) &&
            isStepConfirmed(api.steps, SettlementStep.Expense) &&
            isStepConfirmed(api.steps, SettlementStep.Lien),
    },
    expenses: [],
    deposits: [],
    liens: [],
    steps: api.steps ?? [],
    feeChargeId: api.feeChargeId ?? null,
    existingSettlementFeeAmount: api.existingSettlementFeeAmount ?? null,
    blobPath: api.statementBlobPath ?? null,
    previewBlobPath: api.previewStatementBlobPath ?? null,
    completedAt: api.completedAt ?? null,
    canReset: api.canReset ?? true,
    resetBlockedReason: api.resetBlockedReason ?? null,
    locked: undefined,
    createDate: '',
    modifiedDate: '',
    settlementInvoiceId: api.settlementInvoiceId ?? null,
    invoiceStatus: api.invoiceStatus ?? null,
    canFinalizeInvoice: api.canFinalizeInvoice ?? false,
    canUndoInvoice: api.canUndoInvoice ?? false,
    invoiceBlockedReason: api.invoiceBlockedReason ?? null,
    trustPaymentId: api.trustPaymentId ?? null,
    trustPaymentApplied: api.trustPaymentApplied,
    qboDisbursementId: api.qboDisbursementId ?? null,
    clientDisbursementPushed:
      api.clientDisbursementPushed ?? !!api.qboDisbursementId,
    // Permission flags for UI display only (show/hide buttons).
    // These are derived from business data as a fallback when explicit permissions are not provided.
    // IMPORTANT: Backend ALWAYS validates authorization on actual API requests.
    // Client-side state cannot bypass backend authorization checks.
    canPushClientDisbursement:
      api.canPushClientDisbursement ??
      (!!api.trustPaymentId && !(api.clientDisbursementPushed ?? !!api.qboDisbursementId)),
    canUndoClientDisbursement:
      api.canUndoClientDisbursement ??
      (api.clientDisbursementPushed ?? !!api.qboDisbursementId),
    invoiceAmount: api.invoiceBalance ?? api.invoiceAmount ?? null,
    invoiceBalance: api.invoiceBalance ?? api.invoiceAmount ?? null,
    trustBalance: api.trustBalance ?? null,
    canPayFromTrust: api.canPayFromTrust,
    canUndoTrustPayment:
      api.canUndoTrustPayment ??
      (!!api.trustPaymentId && !(api.clientDisbursementPushed ?? !!api.qboDisbursementId)),
    trustPaymentBlockedReason:
      api.undoTrustPaymentBlockedReason ?? api.trustPaymentBlockedReason ?? null,
    undoTrustPaymentBlockedReason: api.undoTrustPaymentBlockedReason ?? null,
    disbursementBlockedReason: api.disbursementBlockedReason ?? null,
    undoDisbursementBlockedReason: api.undoDisbursementBlockedReason ?? null,
    depositsFromDate: api.depositsFromDate ?? null,
    expenseFromDate: api.expenseFromDate ?? null,
    settlementDate: api.settlementDate ?? null,
    companyExpenseInterestRate: api.companyExpenseInterestRate ?? null,
    noExpenseInterest: api.noExpenseInterest ?? false,
    expenseInterestRateUsed: api.expenseInterestRateUsed ?? null,
    interestExpenseId: api.interestExpenseId ?? null,
    warnings: api.warnings ?? null,
  } as SettlementResponse;
}

/**
 * Map the internal frontend PATCH shape to the backend API shape.
 */
export function mapUpdateToApi(
  patch: UpdateSettlementRequest,
): ApiUpdateSettlementRequest {
  const apiPatch: ApiUpdateSettlementRequest = {};
  if (patch.feePercentage !== undefined) {
    apiPatch.contingencyFeePercentage = patch.feePercentage;
  }
  if (patch.feeMethod !== undefined) {
    apiPatch.feeCalculationMethod = patch.feeMethod
      ? ((FE_TO_API_FEE_METHOD[patch.feeMethod] as ApiFeeCalculationMethod) ??
        (patch.feeMethod as ApiFeeCalculationMethod))
      : null;
  }
  if (patch.expenseArrangement !== undefined) {
    apiPatch.expenseArrangement = patch.expenseArrangement
      ? (FE_TO_API_EXPENSE[patch.expenseArrangement] as ApiExpenseArrangement)
      : null;
  }
  if (patch.roundToNearestDollar !== undefined) {
    apiPatch.roundFeeToNearestDollar = patch.roundToNearestDollar;
  }
  if (patch.settlementDate !== undefined) {
    apiPatch.settlementDate = patch.settlementDate;
  }
  if (patch.expenseFromDate !== undefined) {
    apiPatch.expenseFromDate = patch.expenseFromDate;
  }
  return apiPatch;
}

export const getSettlement = async (
  matterId: string,
): Promise<SettlementResponse> => {
  const { data } = await axios.get<ApiSettlementResponse>(buildUrl(matterId));
  return mapApiToFrontend(data);
};

export const updateSettlement = async (
  matterId: string,
  payload: UpdateSettlementRequest,
): Promise<SettlementResponse> => {
  const apiPayload = mapUpdateToApi(payload);
  const { data } = await axios.patch<ApiSettlementResponse>(
    buildUrl(matterId),
    apiPayload,
  );
  return mapApiToFrontend(data);
};

/** POST /matters/{matterId}/settlement/reset-settings — apply settings then reset all completed wizard steps */
export const resetAndUpdateSettings = async (
  matterId: string,
  payload: UpdateSettlementRequest,
): Promise<SettlementResponse> => {
  const apiPayload = mapUpdateToApi(payload);
  const { data } = await axios.post<ApiSettlementResponse>(
    `${buildUrl(matterId)}/reset-settings`,
    apiPayload,
  );
  return mapApiToFrontend(data);
};

/**
 * Map the frontend settings to the API create request shape.
 */
export function mapCreateToApi(
  settings: SettlementSettings,
): ApiCreateSettlementRequest {
  const request: ApiCreateSettlementRequest = {};
  if (settings.feePercentage != null) {
    request.contingencyFeePercentage = settings.feePercentage;
  }
  if (settings.feeMethod != null) {
    request.feeCalculationMethod =
      (FE_TO_API_FEE_METHOD[settings.feeMethod] as ApiFeeCalculationMethod) ??
      (settings.feeMethod as unknown as ApiFeeCalculationMethod);
  }
  if (settings.expenseArrangement != null) {
    request.expenseArrangement = FE_TO_API_EXPENSE[
      settings.expenseArrangement
    ] as ApiExpenseArrangement;
  }
  if (settings.roundToNearestDollar !== undefined) {
    request.roundFeeToNearestDollar = settings.roundToNearestDollar;
  }
  if (settings.expenseFromDate != null) {
    request.expenseFromDate = settings.expenseFromDate;
  }
  if (settings.settlementDate != null) {
    request.settlementDate = settings.settlementDate;
  }
  return request;
}

/** POST /matters/{id}/settlement — create a new settlement with settings (idempotent) */
export const createSettlement = async (
  matterId: string,
  settings: SettlementSettings,
): Promise<SettlementResponse> => {
  const apiPayload = mapCreateToApi(settings);
  const { data } = await axios.post<ApiSettlementResponse>(
    buildUrl(matterId),
    apiPayload,
  );
  return mapApiToFrontend(data);
};

/**
 * GET /matters/{id}/settlement/deposits — live trust deposits from QBO.
 * startDate/endDate (yyyy-MM-dd) are passed through to filter the QBO query.
 * The backend has no default, so callers should always supply a startDate to
 * avoid the slow all-time fetch.
 */
export const getDeposits = async (
  matterId: string,
  startDate?: string,
  endDate?: string,
): Promise<DepositsResponse> => {
  const params: Record<string, string> = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  const { data } = await axios.get<DepositsResponse>(
    `${buildUrl(matterId)}/deposits`,
    { params },
  );
  return data;
};

/** GET /settlements/{settlementId} — optional aggregate lookup by settlementId */
export const getSettlementById = async (
  settlementId: string,
): Promise<SettlementResponse> => {
  const { data } = await axios.get<ApiSettlementResponse>(
    `/settlements/${settlementId}`,
  );
  return mapApiToFrontend(data);
};

/** POST /settlements/{settlementId}/deposit/confirm — freeze selected deposits and run calc */
export const confirmDeposit = async (
  settlementId: string,
  payload: ConfirmDepositPayload,
): Promise<SettlementResponse> => {
  const { data } = await axios.post<ApiSettlementResponse>(
    `/settlements/${settlementId}/deposit/confirm`,
    payload,
  );
  return mapApiToFrontend(data);
};

// ── Step 2: Expenses ──────────────────────────────────────

export interface MatterExpensesSort {
  orderby?: string;
  order?: 'asc' | 'desc';
}

/** GET /v1/expenses?matterId={id}&billable=billable&orderby=&order= — load unbilled billable expenses for the matter */
export const getMatterExpenses = async (
  matterId: string,
  sort?: MatterExpensesSort,
): Promise<MatterExpenseListResponse> => {
  const { data } = await axios.get<MatterExpenseListResponse>('/expenses', {
    params: {
      matterId,
      billable: 'billable',
      orderby: sort?.orderby,
      order: sort?.order,
    },
  });
  return data;
};

/**
 * GET /v1/expenses?matterId={id}&billed=true&billable=billable — case costs already billed
 * (and possibly paid) for the matter, outside the unbilled batch confirmed in Step 2. ClientPays
 * only: preview of what backend's ConfirmExpensesAsync folds into Total Case Costs, so Step 2
 * shows the same figure that gets saved on confirm (sc-46645).
 */
export const getMatterBilledExpenses = async (
  matterId: string,
): Promise<MatterExpenseListResponse> => {
  const { data } = await axios.get<MatterExpenseListResponse>('/expenses', {
    params: {
      matterId,
      billed: 'true',
      billable: 'billable',
    },
  });
  return data;
};

/** POST /settlements/{settlementId}/expenses/confirm — lock expenses on the settlement */
export const confirmExpenses = async (
  settlementId: string,
  payload: ConfirmExpensesRequest,
): Promise<SettlementResponse> => {
  const { data } = await axios.post<ApiSettlementResponse>(
    `/settlements/${settlementId}/expenses/confirm`,
    payload,
  );
  return mapApiToFrontend(data);
};

export const confirmExpenseInterest = async (
  settlementId: string,
  payload: ConfirmExpenseInterestRequest,
): Promise<SettlementResponse> => {
  const { data } = await axios.post<ApiSettlementResponse>(
    `/settlements/${settlementId}/expenses/confirm-interest`,
    payload,
  );
  return mapApiToFrontend(data);
};

/**
 * GET /settlements/{settlementId}/client-payments
 * Returns live payments + frozen selections from prior confirmation.
 */
export const getClientPayments = async (
  settlementId: string,
): Promise<ClientPaymentsApiResponse> => {
  const { data } = await axios.get<ClientPaymentsApiResponse>(
    `/settlements/${settlementId}/client-payments`,
  );
  return data;
};

/** POST /settlements/{settlementId}/client-payments/confirm — freeze selected payments */
export const confirmClientPayments = async (
  settlementId: string,
  payload: ConfirmClientPaymentsPayload,
): Promise<SettlementResponse> => {
  const { data } = await axios.post<ApiSettlementResponse>(
    `/settlements/${settlementId}/client-payments/confirm`,
    payload,
  );
  return mapApiToFrontend(data);
};

/** POST /settlements/{settlementId}/statement/generate — trigger async PDF generation */
export const generateStatement = async (settlementId: string, preview = false): Promise<void> => {
  await axios.post(`/settlements/${settlementId}/statement/generate`, null, {
    params: preview ? { preview: true } : undefined,
  });
};

/** POST /settlements/{settlementId}/reset — reset settlement so wizard can be re-run */
export const resetSettlement = async (
  settlementId: string,
): Promise<SettlementResponse> => {
  const { data } = await axios.post<ApiSettlementResponse>(
    `/settlements/${settlementId}/reset`,
  );
  return mapApiToFrontend(data);
};

// The serialiser omits nulls, so every optional field is defaulted here rather than at
// each call site — matching how mapApiToFrontend treats canReset/resetBlockedReason.
const mapBlockingSettlement = (
  api: ApiBlockingSettlement,
): BlockingSettlement => ({
  settlementId: api.settlementId,
  matterId: api.matterId,
  matterName: api.matterName ?? '',
  canReset: api.canReset ?? true,
  resetBlockedReason: api.resetBlockedReason ?? null,
});

/** GET /clients/{clientId}/settlements/blocking — the client's settlements past Step 1 */
export const getBlockingSettlementsForClient = async (
  clientId: string,
): Promise<BlockingSettlement[]> => {
  const { data } = await axios.get<ApiBlockingSettlementsResponse>(
    `/clients/${clientId}/settlements/blocking`,
  );
  return (data?.settlements ?? []).map(mapBlockingSettlement);
};

/**
 * POST /clients/{clientId}/settlements/reset — reset every blocking settlement under
 * the client in one transaction. Returns the ids it cleared; all-or-nothing, so a 409
 * naming a blocked matter means nothing was reset.
 */
export const resetSettlementsForClient = async (
  clientId: string,
): Promise<string[]> => {
  const { data } = await axios.post<ApiClientSettlementsResetResponse>(
    `/clients/${clientId}/settlements/reset`,
  );
  return data?.settlementIds ?? [];
};

/** GET /settlements/{settlementId}/statement — download the PDF as a Blob via axios (authenticated). */
export const downloadStatementBlob = async (settlementId: string): Promise<Blob> => {
  const { data } = await axios.get(`/settlements/${settlementId}/statement`, {
    responseType: 'blob',
  });
  return data;
};

/** GET /settlements/{settlementId}/statement?preview=true — download the preview PDF as a Blob via axios (authenticated). */
export const downloadPreviewStatementBlob = async (settlementId: string): Promise<Blob> => {
  const { data } = await axios.get(`/settlements/${settlementId}/statement`, {
    responseType: 'blob',
    params: { preview: true },
  });
  return data;
};

/** GET /settlements/{settlementId}/attorney-fee — calculated fee + existing charges */
export const getAttorneyFee = async (
  settlementId: string,
): Promise<ApiAttorneyFeeResponse> => {
  const { data } = await axios.get<ApiAttorneyFeeResponse>(
    `/settlements/${settlementId}/attorney-fee`,
  );
  return data;
};

/** POST /settlements/{settlementId}/attorney-fee/record — create new fee charge */
export const recordAttorneyFee = async (
  settlementId: string,
  payload: RecordFeeRequest,
): Promise<ApiFeeEntry> => {
  const { data } = await axios.post<ApiFeeEntry>(
    `/settlements/${settlementId}/attorney-fee/record`,
    payload,
  );
  return data;
};

/** PATCH /settlements/{settlementId}/attorney-fee/{chargeId} — update existing fee */
export const updateAttorneyFee = async (
  settlementId: string,
  chargeId: string,
  payload: RecordFeeRequest,
): Promise<ApiFeeEntry> => {
  const { data } = await axios.patch<ApiFeeEntry>(
    `/settlements/${settlementId}/attorney-fee/${chargeId}`,
    payload,
  );
  return data;
};

/** POST /settlements/{settlementId}/attorney-fee/confirm — link fee and advance step */
export const confirmAttorneyFee = async (
  settlementId: string,
  payload: ConfirmAttorneyFeePayload,
): Promise<SettlementResponse> => {
  const { data } = await axios.post<ApiSettlementResponse>(
    `/settlements/${settlementId}/attorney-fee/confirm`,
    payload,
  );
  return mapApiToFrontend(data);
};

// ── Step 4: Liens ──────────────────────────────────────

/** GET /settlements/{settlementId}/liens/from-table — grouped liens from Liens tab (flipper ON) */
export const getLiensFromTable = async (
  settlementId: string,
): Promise<FromTableLiensResponse> => {
  const { data } = await axios.get<FromTableLiensResponse>(
    `/settlements/${settlementId}/liens/from-table`,
  );
  return data;
};

/** POST /settlements/{settlementId}/liens/confirm — persist selected lien snapshots, run calc, mark step 4 complete */
export const confirmLiens = async (
  settlementId: string,
  payload: ConfirmLiensRequest,
): Promise<SettlementResponse> => {
  const { data } = await axios.post<ApiSettlementResponse>(
    `/settlements/${settlementId}/liens/confirm`,
    payload,
  );
  return mapApiToFrontend(data);
};

// ── Post-Settlement Actions (V2-FE-1) ──────────────────────

/** GET /matters/{matterId}/trust-balance — trust account balance for the matter */
export const getTrustBalance = async (
  matterId: string,
): Promise<TrustBalanceResponse> => {
  const { data } = await axios.get<TrustBalanceResponse>(
    `/matters/${matterId}/trust-balance`,
  );
  return data;
};

/** POST /settlements/{settlementId}/invoice/finalize — create and finalize the settlement invoice (V2-1.1) */
export const finalizeInvoice = async (
  settlementId: string,
): Promise<SettlementResponse> => {
  const { data } = await axios.post<ApiSettlementResponse>(
    `/settlements/${settlementId}/invoice/finalize`,
  );
  return mapApiToFrontend(data);
};

/** POST /settlements/{settlementId}/invoice/undo — undo the finalized invoice (V2-1.1) */
export const undoInvoice = async (
  settlementId: string,
): Promise<SettlementResponse> => {
  const { data } = await axios.post<ApiSettlementResponse>(
    `/settlements/${settlementId}/invoice/undo`,
  );
  return mapApiToFrontend(data);
};

/** POST /settlements/{settlementId}/trust-payment — disburse invoice amount from trust (V2-1.4) */
export const payFromTrust = async (
  settlementId: string,
): Promise<PostSettlementActionResponse> => {
  const { data } = await axios.post<ApiSettlementResponse & { message?: string }>(
    `/settlements/${settlementId}/trust-payment`,
  );
  return {
    settlement: mapApiToFrontend(data),
    message: typeof data?.message === 'string' ? data.message : null,
  };
};

/** DELETE /settlements/{settlementId}/trust-payment — undo the trust disbursement (V2-1.4) */
export const undoTrustPayment = async (
  settlementId: string,
): Promise<SettlementResponse> => {
  const { data } = await axios.post<ApiSettlementResponse>(
    `/settlements/${settlementId}/trust-payment/undo`,
  );
  return mapApiToFrontend(data);
};

/** POST /settlements/{settlementId}/client-disbursement — push client net proceeds to QBO (V2-1.5) */
export const pushToQbo = async (
  settlementId: string,
): Promise<PostSettlementActionResponse> => {
  const { data } = await axios.post<ApiSettlementResponse & { message?: string }>(
    `/settlements/${settlementId}/client-disbursement`,
  );
  return {
    settlement: mapApiToFrontend(data),
    message: typeof data?.message === 'string' ? data.message : null,
  };
};

/** DELETE /settlements/{settlementId}/client-disbursement — undo the client disbursement entry (V2-1.5) */
export const undoQboDisbursement = async (
  settlementId: string,
): Promise<SettlementResponse> => {
  const { data } = await axios.post<ApiSettlementResponse>(
    `/settlements/${settlementId}/client-disbursement/undo`,
  );
  return mapApiToFrontend(data);
};

/** GET /settlements/{settlementId}/trust-statement — download trust statement PDF (V2-1.2) */
export const downloadTrustStatementBlob = async (
  settlementId: string,
): Promise<Blob> => {
  const { data } = await axios.get(`/settlements/${settlementId}/trust-statement`, {
    responseType: 'blob',
  });
  return data;
};

/**
 * GET /settlement-statement-template/keywords — get supported keywords.
 * When `settlementId` is provided (matter page), the backend resolves each
 * keyword's real `value` for that settlement. Omit it for the firm template
 * page, where the frontend supplies sample values instead.
 */
export const getSettlementStatementKeywords = async (
  settlementId?: string,
): Promise<SettlementKeywordInfo[]> => {
  const { data } = await axios.get<SettlementKeywordInfo[]>(
    '/settlement-statement-template/keywords',
    settlementId ? { params: { settlementId } } : undefined,
  );
  return data;
};

/** GET /settlement-statement-template — get settlement statement template */
export const getSettlementStatementTemplate = async (): Promise<SettlementStatementTemplateDto | null> => {
  try {
    const { data } = await axios.get<SettlementStatementTemplateDto>(
      '/settlement-statement-template',
    );
    return data;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

/** POST /settlement-statement-template — upsert settlement statement template (idempotent) */
export const updateSettlementStatementTemplate = async (
  payload: NarrativeContentInput,
): Promise<SettlementStatementTemplateDto> => {
  const { data } = await axios.post<SettlementStatementTemplateDto>(
    '/settlement-statement-template',
    payload,
  );
  return data;
};

/** GET /settlements/{settlementId}/statement/narrative — fork-on-first-read */
export const getNarrative = async (
  settlementId: string,
): Promise<SettlementStatementNarrativeDto> => {
  const { data } = await axios.get<SettlementStatementNarrativeDto>(
    `/settlements/${settlementId}/statement/narrative`,
  );
  return data;
};

/** PUT /settlements/{settlementId}/statement/narrative — save narrative content */
export const saveNarrative = async (
  settlementId: string,
  payload: NarrativeContentInput,
): Promise<SettlementStatementNarrativeDto> => {
  const { data } = await axios.post<SettlementStatementNarrativeDto>(
    `/settlements/${settlementId}/statement/narrative`,
    payload,
  );
  return data;
};

/** POST /settlements/{settlementId}/statement/narrative/restore — restore from firm template */
export const restoreNarrativeFromTemplate = async (
  settlementId: string,
): Promise<SettlementStatementNarrativeDto> => {
  const { data } = await axios.post<SettlementStatementNarrativeDto>(
    `/settlements/${settlementId}/statement/narrative/restore`,
  );
  return data;
};

/** POST /settlements/{settlementId}/statement/narrative/lock — acquire editor lock */
export const acquireNarrativeLock = async (
  settlementId: string,
): Promise<EditorLockDto> => {
  const { data } = await axios.post<EditorLockDto>(
    `/settlements/${settlementId}/statement/narrative/lock`,
  );
  return data;
};

/** DELETE /settlements/{settlementId}/statement/narrative/lock — release editor lock */
export const releaseNarrativeLock = async (settlementId: string): Promise<void> => {
  await axios.delete(`/settlements/${settlementId}/statement/narrative/lock`);
};

/**
 * GET /matters/optionscount — the client's active matter count, for SETTLE:BR-V4-1.
 *
 * Deliberately not `useClientMatterOptionsStore.getMatterCount`, which hits the same
 * endpoint but `$patch`es a shared `matterCount` that `ClientMatterSearch.vue` reads to
 * decide whether to list matters or demand a search term. An analytics read must not
 * change what another component shows.
 *
 * Active-only: the endpoint's `Archived` filter defaults false. It is also scoped to the
 * caller's accessible matters, so this is the client's active matters *visible to this
 * user*, not a firm-wide roster.
 */
export const getClientMatterCount = async (
  clientId: string,
): Promise<number> => {
  const { data } = await axios.get<number>('/matters/optionscount', {
    params: { clientId },
  });
  return data;
};
